import { Component, Input, Output, EventEmitter, HostListener, ElementRef, OnInit, OnDestroy } from '@angular/core';

export interface ContextMenuItem {
  label: string;
  icon?: string;
  action: string;
  disabled?: boolean;
  divider?: boolean;
  color?: string; // For status color indicators (square)
  iconColor?: string; // For icon color (separate from square indicator)
  children?: ContextMenuItem[]; // For submenus
}

@Component({
  selector: 'app-context-menu',
  standalone: false,
  templateUrl: './context-menu.component.html',
  styleUrl: './context-menu.component.css'
})
export class ContextMenuComponent implements OnInit, OnDestroy {
  @Input() items: ContextMenuItem[] = [];
  @Input() x: number = 0;
  @Input() y: number = 0;
  @Input() rowData: any = null;
  @Output() itemClick = new EventEmitter<{ action: string; rowData: any }>();
  @Output() close = new EventEmitter<void>();

  activeSubmenu: { action: string; items: ContextMenuItem[] } | null = null;
  submenuTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(private elementRef: ElementRef) { }

  ngOnInit(): void {
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('contextmenu', this.handleDocumentClick);
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('contextmenu', this.handleDocumentClick);
  }

  @HostListener('click', ['$event'])
  onMenuClick(event: Event): void {
    event.stopPropagation();
  }

  private handleDocumentClick = (event: MouseEvent): void => {
    // Use requestAnimationFrame to ensure click events process first
    // This prevents the menu from closing before the click event completes
    requestAnimationFrame(() => {
      const target = event.target as Node;
      if (target && !this.elementRef.nativeElement.contains(target)) {
        // Add a small delay to allow navigation/actions to start
        setTimeout(() => {
          this.closeMenu();
        }, 100);
      }
    });
  };

  onItemClick(item: ContextMenuItem, event: Event): void {
    // Stop propagation to prevent parent handlers from interfering
    event.stopPropagation();
    
    // DO NOT call preventDefault() - it prevents navigation and other default behaviors
    // Only prevent default for disabled items
    if (item.disabled) {
      event.preventDefault();
      return;
    }

    if (item.children && item.children.length > 0) {
      this.toggleSubmenu(item, event);
      return;
    }

    // Emit the event immediately
    this.itemClick.emit({ action: item.action, rowData: this.rowData });

    // Close menu after a small delay to ensure navigation/action starts
    // Use requestAnimationFrame to ensure the event is processed first
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.closeMenu();
      }, 50);
    });
  }

  // Add mousedown handler as backup for better reliability
  onItemMouseDown(item: ContextMenuItem, event: MouseEvent): void {
    // Only handle left mouse button
    if (event.button !== 0) {
      return;
    }
    
    event.stopPropagation();
    
    if (item.disabled) {
      event.preventDefault();
      return;
    }

    if (item.children && item.children.length > 0) {
      // Don't prevent default for submenus
      return;
    }

    // For non-submenu items, emit immediately on mousedown
    // This ensures the action triggers even if click is somehow blocked
    this.itemClick.emit({ action: item.action, rowData: this.rowData });
  }

  onItemMouseEnter(item: ContextMenuItem, event: MouseEvent): void {
    if (this.submenuTimeout) {
      clearTimeout(this.submenuTimeout);
    }

    if (item.children && item.children.length > 0) {
      this.activeSubmenu = { action: item.action, items: item.children };
    } else {
      this.activeSubmenu = null;
    }
  }

  onItemMouseLeave(): void {
    this.submenuTimeout = setTimeout(() => {
      this.activeSubmenu = null;
    }, 300);
  }

  toggleSubmenu(item: ContextMenuItem, event: Event): void {
    if (this.activeSubmenu?.action === item.action) {
      this.activeSubmenu = null;
    } else {
      this.activeSubmenu = { action: item.action, items: item.children || [] };
    }
  }

  closeMenu(): void {
    this.close.emit();
  }

  getMenuStyle(): { [key: string]: string } {
    // If x and y are 0, it's likely a submenu being positioned by CSS
    if (this.x === 0 && this.y === 0) {
      return {
        position: 'static'
      };
    }
    return {
      position: 'fixed',
      left: `${this.x}px`,
      top: `${this.y}px`,
      'z-index': '10000'
    };
  }
}
